import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { API_BASE_URL } from "@/config";
import {
  isShowCatalogCard,
  type ShowCardCopy,
  type ShowCardVendor,
  type ShowCatalogCard,
} from "@/lib/shows/showCardFinding";
import { isRecord, isStringOrNull, validatedJson } from "@/lib/ui/validatedJson";

export type PublicShowInventory = {
  cards: ShowCatalogCard[];
  zero_result_cause?: "no_catalog_match" | "not_listed" | null;
};
export type PublicShowCardDetail = { card: ShowCatalogCard; vendors: ShowCardVendor[]; };
export type PublicShowVendorCard = { card: ShowCatalogCard; copies: ShowCardCopy[]; };
export type PublicShowVendorDetail = {
  participation_id: string;
  name: string;
  table_numbers: string[];
  cards: PublicShowVendorCard[];
  storefront_available: boolean;
};

class PublicShowInventoryError extends Error {
  constructor(message: string, readonly status?: number) {
    super(message);
  }
}

/** A Show read that 404s is a settled answer, not a flaky one: the Vendor sold the last copy and
 * the card left the Show's public inventory, so retrying can only 404 again. Callers showing a
 * saved list need to say "no longer listed" rather than "try again". */
export const isMissingShowListing = (error: unknown): boolean =>
  error instanceof PublicShowInventoryError && error.status === 404;

const stringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === "string");

const count = (value: unknown): value is number =>
  typeof value === "number" && Number.isInteger(value) && value >= 0;

function isCopy(value: unknown): value is ShowCardCopy {
  return isRecord(value) && isStringOrNull(value.condition) && count(value.quantity)
    && typeof value.is_graded === "boolean"
    && isStringOrNull(value.grading_company) && isStringOrNull(value.grade);
}

function isVendor(value: unknown): value is ShowCardVendor {
  return isRecord(value) && typeof value.participation_id === "string"
    && typeof value.name === "string" && stringArray(value.table_numbers)
    && Array.isArray(value.copies) && value.copies.every(isCopy);
}

const isInventory = (value: unknown): value is PublicShowInventory =>
  isRecord(value) && Array.isArray(value.cards) && value.cards.every(isShowCatalogCard)
  && (value.zero_result_cause === undefined || value.zero_result_cause === null
    || value.zero_result_cause === "no_catalog_match" || value.zero_result_cause === "not_listed");

const isCardDetail = (value: unknown): value is PublicShowCardDetail =>
  isRecord(value) && isShowCatalogCard(value.card) && Array.isArray(value.vendors)
  && value.vendors.every(isVendor);

const isVendorDetail = (value: unknown): value is PublicShowVendorDetail =>
  isRecord(value) && typeof value.participation_id === "string" && typeof value.name === "string"
  && stringArray(value.table_numbers) && typeof value.storefront_available === "boolean"
  && Array.isArray(value.cards)
  && value.cards.every((item) =>
    isRecord(item) && isShowCatalogCard(item.card) && Array.isArray(item.copies)
    && item.copies.every(isCopy)
  );

async function publicJson<T>(
  path: string,
  signal: AbortSignal,
  matches: (value: unknown) => value is T,
): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, { signal });
  if (!response.ok) {
    throw new PublicShowInventoryError(
      response.status === 404
        ? "This Show listing is no longer available."
        : "Card search is unavailable.",
      response.status,
    );
  }
  return validatedJson(response, matches, "Show inventory response was invalid.");
}

const showPath = (showId: string) => `/shows/${encodeURIComponent(showId)}`;

export function fetchPublicShowInventory(
  showId: string,
  query: string,
  signal: AbortSignal,
): Promise<PublicShowInventory> {
  const search = new URLSearchParams({ q: query.trim() });
  return publicJson<PublicShowInventory>(
    `${showPath(showId)}/inventory?${search.toString()}`,
    signal,
    isInventory,
  );
}

export function fetchPublicShowCard(
  showId: string,
  cardId: string,
  signal: AbortSignal,
): Promise<PublicShowCardDetail> {
  return publicJson<PublicShowCardDetail>(
    `${showPath(showId)}/cards/${encodeURIComponent(cardId)}`,
    signal,
    isCardDetail,
  );
}

/** Live Show inventory is deliberately network-only; it never enters the Published-artifact cache. */
export function usePublicShowInventory(showId: string | undefined, query: string) {
  const q = query.trim();
  return useQuery({
    queryKey: ["public-show-inventory", showId, q],
    enabled: !!showId && !!q,
    queryFn: ({ signal }) => fetchPublicShowInventory(showId!, q, signal),
    placeholderData: keepPreviousData,
    gcTime: 0,
    retry: 1,
    staleTime: 0,
  });
}

export function usePublicShowCard(
  showId: string | undefined,
  cardId: string | undefined,
) {
  return useQuery({
    queryKey: ["public-show-card", showId, cardId],
    enabled: !!showId && !!cardId,
    queryFn: ({ signal }) => fetchPublicShowCard(showId!, cardId!, signal),
    gcTime: 0,
    retry: 1,
    staleTime: 0,
  });
}

export function usePublicShowVendor(
  showId: string | undefined,
  participationId: string | undefined,
) {
  return useQuery({
    queryKey: ["public-show-vendor", showId, participationId],
    enabled: !!showId && !!participationId,
    queryFn: ({ signal }) =>
      publicJson<PublicShowVendorDetail>(
        `${showPath(showId!)}/vendors/${encodeURIComponent(participationId!)}`,
        signal,
        isVendorDetail,
      ),
    gcTime: 0,
    retry: 1,
    staleTime: 0,
  });
}

export function publicShowVendorStorefrontUrl(
  showId: string,
  participationId: string,
  query: string,
): string {
  const search = query.trim() ? `?${new URLSearchParams({ q: query.trim() })}` : "";
  return `${API_BASE_URL}${showPath(showId)}/vendors/${
    encodeURIComponent(participationId)
  }/storefront${search}`;
}
